import EdgeFunctionClient from './edgeFunctionClient';
import LocalStorageService from './localStorageService';
import supabase from './supabaseClient';

class KeyService {
  // Helper method to check if user is logged in
  static async isUserLoggedIn() {
    console.log('KeyService: Checking if user is logged in...');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const isLoggedIn = !!session;
      console.log('KeyService: User logged in:', isLoggedIn, 'Session:', session);
      return isLoggedIn;
    } catch (error) {
      console.error('KeyService: Error checking login status:', error);
      return false;
    }
  }

  // Key-related methods
  static async createKey({ encryptionKey, name, subject }) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        const data = await EdgeFunctionClient.call('keys-create', {
          encryption_key: encryptionKey,
          name
        });
        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      // Use local storage for non-logged-in users
      return LocalStorageService.saveKey({ encryptionKey, name, subject });
    }
  }

  static async getKeys() {
    console.log('KeyService: Starting getKeys...');
    const isLoggedIn = await this.isUserLoggedIn();
    console.log('KeyService: isLoggedIn =', isLoggedIn);
    
    if (isLoggedIn) {
      try {
        console.log('KeyService: Fetching remote keys...');
        
        // Add timeout for remote key fetching
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Remote key fetch timed out')), 8000);
        });
        
        const remoteKeysPromise = EdgeFunctionClient.call('keys-get-all');
        
        // Get keys from Supabase with timeout
        const remoteKeys = await Promise.race([remoteKeysPromise, timeoutPromise]);
        console.log('KeyService: Remote keys received:', remoteKeys);
        
        // Also get local keys if they exist
        const localKeys = LocalStorageService.getKeys();
        console.log('KeyService: Local keys received:', localKeys);
        
        // Mark local keys as such and combine with remote keys
        const markedLocalKeys = localKeys.map(key => ({
          ...key,
          isLocal: true,
          source: 'local'
        }));
        
        const markedRemoteKeys = remoteKeys.map(key => ({
          ...key,
          isLocal: false,
          source: 'remote'
        }));
        
        // Return combined array with local keys first (so user sees them prominently)
        const combinedKeys = [...markedLocalKeys, ...markedRemoteKeys];
        console.log('KeyService: Combined keys:', combinedKeys);
        return combinedKeys;
      } catch (error) {
        console.error('KeyService: Error getting remote keys:', error);
        throw new Error(error.message);
      }
    } else {
      console.log('KeyService: User not logged in, getting local keys only...');
      // Return local storage keys for non-logged-in users
      const localKeys = LocalStorageService.getKeys();
      const markedKeys = localKeys.map(key => ({
        ...key,
        isLocal: true,
        source: 'local'
      }));
      console.log('KeyService: Local keys for non-logged-in user:', markedKeys);
      return markedKeys;
    }
  }

  static async deleteKey(id, isLocal = false) {
    console.log(`KeyService: Deleting key ${id}, isLocal: ${isLocal}`);
    
    // If the key is explicitly marked as local, delete from local storage
    if (isLocal) {
      console.log('KeyService: Deleting local key from localStorage');
      return LocalStorageService.deleteKey(id);
    }
    
    // Otherwise, check if user is logged in and try remote deletion
    const isLoggedIn = await this.isUserLoggedIn();
    console.log(`KeyService: User logged in: ${isLoggedIn}, attempting remote deletion`);
    
    if (isLoggedIn) {
      try {
        await EdgeFunctionClient.call('keys-delete', { id });
        console.log('KeyService: Remote key deletion successful');
        return true;
      } catch (error) {
        console.error('KeyService: Remote key deletion failed:', error);
        throw new Error(error.message);
      }
    } else {
      // Use local storage for non-logged-in users
      console.log('KeyService: User not logged in, deleting from localStorage');
      return LocalStorageService.deleteKey(id);
    }
  }

  static async updateKey({ id, name }) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        const data = await EdgeFunctionClient.call('keys-update', { id, name });
        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      // Use local storage for non-logged-in users
      return LocalStorageService.updateKey({ id, name });
    }
  }

  static async getById(id) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        const data = await EdgeFunctionClient.call('keys-get-by-id', { id });
        return data;
      } catch (error) {
        throw error;
      }
    } else {
      // Use local storage for non-logged-in users
      return LocalStorageService.getKeyById(id);
    }
  }

  // Message-related methods
  static async createMessage({ subject }) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        const data = await EdgeFunctionClient.call('messages-create', { subject });
        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      // For non-logged-in users, we'll handle messages differently in upload method
      // This method is primarily used by logged-in users
      throw new Error('Message creation requires user authentication');
    }
  }

  static async getMessages() {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        const data = await EdgeFunctionClient.call('messages-get-all-with-keys');
        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      // For non-logged-in users, we don't store messages locally, only keys
      // Return empty array since messages are only available for logged-in users
      return [];
    }
  }

  static async getMessageById(id) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        const data = await EdgeFunctionClient.call('messages-get-by-id-with-keys', { id });
        return data;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      // For non-logged-in users, messages are not stored locally
      throw new Error('Messages are only available for logged-in users');
    }
  }

  static async deleteMessage(id) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (isLoggedIn) {
      try {
        await EdgeFunctionClient.call('messages-delete', { id });
        return true;
      } catch (error) {
        throw new Error(error.message);
      }
    } else {
      // For non-logged-in users, messages are not stored locally
      throw new Error('Messages are only available for logged-in users');
    }
  }

  // Message-Key relationship methods
  static async addKeyToMessage(messageId, keyId) {
    try {
      const data = await EdgeFunctionClient.call('message-keys-add', {
        message_id: messageId,
        key_id: keyId
      });
      return data;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  static async removeKeyFromMessage(messageId, keyId) {
    try {
      await EdgeFunctionClient.call('message-keys-remove', {
        message_id: messageId,
        key_id: keyId
      });
      return true;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  // Combined workflow for uploading a new message with encryption key
  static async upload({ encryptionKey, emails, subject, name }) {
    try {
      const data = await EdgeFunctionClient.call('upload-message-with-key', {
        encryption_key: encryptionKey,
        emails,
        subject,
        name
      });
      return data;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  // For backward compatibility
  static async get() {
    return this.getKeys();
  }

  static async getShared() {
    return this.getMessages();
  }

  static async delete(id, isLocal) {
    return this.deleteKey(id, isLocal);
  }

  static async patch({ _id, ...updates }) {
    return this.updateKey({ id: _id, ...updates });
  }

  static async shareKey(keyId) {
    try {
      const data = await EdgeFunctionClient.call('share-key', { key_id: keyId });
      return data;
    } catch (error) {
      throw new Error(error.message);
    }
  }

  // Method to sync a single local key to Supabase
  static async syncLocalKeyToSupabase(localKey) {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (!isLoggedIn) {
      throw new Error('User must be logged in to sync keys');
    }

    try {
      // Create the key in Supabase
      const syncedKey = await this.createKey({
        encryptionKey: localKey.encryption_key,
        name: localKey.name
      });
      
      // Remove from local storage
      LocalStorageService.deleteKey(localKey.id);
      
      return syncedKey;
    } catch (error) {
      throw new Error(`Failed to sync key: ${error.message}`);
    }
  }

  // Method to sync all local keys to Supabase
  static async syncAllLocalKeysToSupabase() {
    const isLoggedIn = await this.isUserLoggedIn();
    
    if (!isLoggedIn) {
      throw new Error('User must be logged in to sync keys');
    }

    try {
      const syncResults = await LocalStorageService.syncToSupabase(this);
      return syncResults;
    } catch (error) {
      throw new Error(`Failed to sync all keys: ${error.message}`);
    }
  }
}

export default KeyService;
